// Helpers for rendering the parts catalogue that arrives on the `parts_data` stream event.
//
// Those URLs come straight from the parts API, so they are byte-exact. The same links, after
// the model has re-typed them into prose, are not: "toyota,2025,camry" came through as
// "toyota,025,camy" and eBay's %3A/%2C as %A, leaving every link broken. So when we have the
// structured payload we render cards from it and remove the model's transcription from the
// text, rather than showing a corrupted duplicate underneath the good cards.

const safeText = (value = '') => (typeof value === 'string' ? value : String(value ?? ''));

// Retailers the parts tools can return. Anchored to these names so ordinary prose mentioning
// a shop ("cheaper at AutoZone") is never mistaken for a link line.
const RETAILER_NAMES = 'eBay|AutoZone|RockAuto|NAPA|Amazon|OEM\\s+dealer\\s+search';

// "Buy Links" / "Sourcing links" heading on a line of its own.
// The trailing \n? takes the line break with it, so removing a heading does not leave a blank
// line where it used to be.
const LINK_HEADING_RE = new RegExp(
    '^[ \\t]*(?:\\*\\*)?(?:buy|shop|shopping|sourcing)\\s+links?(?:\\*\\*)?[ \\t]*:?[ \\t]*$\\n?',
    'gim',
);

// "Retailer: https://… | Price: $x | Image: …" — the URL plus any pipe-separated metadata
// trailing it. The model usually crams every retailer onto one line, which is why this has to
// match mid-line rather than per-line.
const RETAILER_LINK_RE = new RegExp(
    `(?:${RETAILER_NAMES})[ \\t]*:[ \\t]*https?://[^\\s|]+(?:[ \\t]*\\|[^|\\n]*)*`,
    'gi',
);

// A bare markdown/plain URL to one of those retailers, left behind without its label.
const BARE_RETAILER_URL_RE =
    /https?:\/\/(?:www\.)?(?:ebay|autozone|rockauto|napaonline|amazon)\.[^\s)>\]]*/gi;

/**
 * Remove retailer link text from prose. Only call this when structured parts data is present —
 * otherwise the text links are the only ones the user has, and stripping them loses data.
 */
export function stripRetailerLinkProse(content = '') {
    return safeText(content)
        .replace(LINK_HEADING_RE, '')
        .replace(RETAILER_LINK_RE, '')
        .replace(BARE_RETAILER_URL_RE, '')
        // Tidy what the removals left. A bullet whose only content was a link is deleted
        // outright — line break included — so a stripped list closes up instead of leaving a
        // gap in the middle of the paragraph.
        .replace(/[ \t]*\|[ \t]*$/gm, '')
        .replace(/[ \t]+$/gm, '')
        .replace(/^[ \t]*[-*•][ \t]*$\n?/gm, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Merge a newly arrived `parts_data` payload into what this turn has already collected.
 *
 * One question can fire several parts tools, and they return different halves of the answer:
 * lookup_parts and get_part_ordering_guide carry sourcing_links, while other parts tools
 * return eBay listings only. Replacing on each event let whichever tool finished last decide
 * the cards — and when that one had no sourcing_links, every retailer card (AutoZone, NAPA,
 * RockAuto, Amazon, OEM search) vanished even though it had been fetched successfully.
 * Accumulating instead means each tool can only add to the picture, never subtract from it.
 */
export function mergePartsData(previous, incoming) {
    if (!incoming) return previous || null;
    if (!previous) return incoming;

    const dedupeBy = (rows, key) => {
        const seen = new Set();
        return rows.filter((row) => {
            const value = row && row[key];
            if (!value || seen.has(value)) return false;
            seen.add(value);
            return true;
        });
    };

    return {
        ...previous,
        ...incoming,
        // Keep whatever we learned first: a later tool may not echo these back.
        part_description: previous.part_description || incoming.part_description || '',
        vehicle: previous.vehicle || incoming.vehicle || '',
        listings: dedupeBy([...(previous.listings || []), ...(incoming.listings || [])], 'itemWebUrl'),
        retailers: dedupeBy([...(previous.retailers || []), ...(incoming.retailers || [])], 'url'),
    };
}

/**
 * Convert a `parts_data` payload into the row shape LinkListCards renders.
 * `helpers` supplies the host/label utilities that live in ChatPage.
 */
export function buildPartsCards(partsData, helpers = {}) {
    if (!partsData) return [];
    const {
        getHostFromUrl = () => '',
        cleanDisplayText = (v) => safeText(v).trim(),
        titleCaseWords = (v) => safeText(v),
        getLinkCardDetail = () => '',
    } = helpers;

    const listings = Array.isArray(partsData.listings) ? partsData.listings : [];
    const retailers = Array.isArray(partsData.retailers) ? partsData.retailers : [];
    const fallbackDetail = [partsData.vehicle, partsData.part_description]
        .filter(Boolean).join(' ').trim();

    const listingCards = listings
        .filter((item) => item && typeof item === 'object' && item.itemWebUrl)
        .map((item) => ({
            retailer: 'eBay',
            url: item.itemWebUrl,
            host: getHostFromUrl(item.itemWebUrl),
            query: cleanDisplayText(item.title || '') || fallbackDetail,
            price: item.price || '',
            image: item.imageUrl || '',
            seller: item.seller || '',
            feedback: item.seller_feedback || '',
            brand: item.brand || '',
            availability: item.availability || '',
            condition: item.condition || '',
            shipping: item.shipping || '',
            location: item.location || '',
        }));

    const retailerCards = retailers
        .filter((entry) => entry && typeof entry === 'object' && typeof entry.url === 'string' && entry.url)
        .map((entry) => ({
            retailer: entry.retailer
                || titleCaseWords((getHostFromUrl(entry.url).split('.')[0]) || 'Retailer'),
            url: entry.url,
            host: getHostFromUrl(entry.url),
            query: fallbackDetail || getLinkCardDetail(entry.url, 'parts'),
        }));

    return [...listingCards, ...retailerCards];
}
